import firebase from 'firebase/app'
import auth from '@/firebase/auth'
import api from '@/api/api'

const state = {
    user: '',
    users: [],
    nextUserPageToken: '0',
}

const getters = {
    user: (state) => state.user,
    users: (state) => state.users,
}

const mutations = {
    SET_CURRENT_USER: (state, user) => (state.user = user),
    EDIT_CURRENT_USER: (state, user) => (state.user = { ...state.user, ...user }),
    ADD_USER: (state, payload) => {
        state.users.push(payload)
    },
    ADD_USERS: (state, response) => {
        state.users = response.data.users
        state.nextUserPageToken = response.data.pageToken
    },
    EDIT_USER: (state, payload) => {
        state.users = state.users.map(user => {
            if (user.uid === payload.uid) {
                return {
                    ...user,
                    ...payload,
                }
            }

            return user
        })
    },
    DELETE_USER: (state, uid) => {
        state.users = state.users.filter(user => user.uid !== uid)
    },
}

const actions = {
    _createUser: async ({ commit }, payload) => {
        const token = await auth
            .currentUser
            .getIdToken()
            .then((idToken) => {
                return idToken
            })

        return api
            .post('/auth/user', payload.form, {
                headers: {
                    token,
                },
            })
            .then(response => {
                if (response.data !== undefined) {
                    commit('ADD_USER', response.data)
                }
            })
    },
    createUser: ({ dispatch }, payload) => {
        if (payload.image) {
            return dispatch('files/upload', { path: 'avatars', file: payload.image }, { root: true })
                .then(url => {
                    payload.form.photoURL = url
                    dispatch('_createUser', payload)
                })
        }

        return dispatch('_createUser', payload)
    },
    _editUser: async ({ commit }, payload) => {
        const token = await auth
            .currentUser
            .getIdToken()
            .then((idToken) => {
                return idToken
            })

        api
            .put('/auth/user/' + payload.uid, {
                ...payload.form,
                updatedUser: auth.currentUser.uid,
            },
            {
                headers: {
                    token,
                },
            })
            .then(response => {
                if (response.status !== 200) {
                    console.log('%c%s:%i', 'color: darkgreen', 'edit user', 36, response)
                    return
                }

                commit('EDIT_USER', {
                    ...payload.form,
                    uid: payload.uid,
                })
            })
    },
    editUser: ({ dispatch, commit }, payload) => {
        if (payload.image) {
            return dispatch('files/upload', { path: 'avatars', file: payload.image }, { root: true })
                .then(url => {
                    payload.form.photoURL = url
                    payload.commit = commit
                    dispatch('_editUser', payload)
                })
        }

        dispatch('_editUser', payload)
    },
    loadUsers: async ({ commit }) => {
        if (state.users.length) return

        await auth
            .currentUser
            .getIdToken()
            .then(async token => {
                await api
                    .get('/auth/user', {
                        headers: {
                            token,
                        },
                    })
                    .then(response => commit('ADD_USERS', response))
                    .catch(err => console.log('%c%s:%i', 'color: darkgreen', 'index.js', 63, err))
            })
    },
    inactivateUser: ({ dispatch }, uid) => {
        const payload = {
            uid,
            form: {
                active: false,
            },
        }

        dispatch('_editUser', payload)
    },
    activateUser: ({ dispatch }, uid) => {
        const payload = {
            uid,
            form: {
                active: true,
            },
        }

        dispatch('_editUser', payload)
    },
    createWithEmailAndPassword: ({ commit }, credentials) => {
        return auth
            .createUserWithEmailAndPassword(credentials.email, credentials.password)
            .then(() => {
                const user = auth
                    .currentUser

                user
                    .updateProfile({
                        displayName: credentials.displayName,
                    })
                    .then(() => {
                        const profile = {
                            displayName: credentials.displayName,
                            email: user.email,
                            emailVerified: user.emailVerified,
                            photoURL: user.photoURL,
                            providerData: user.providerData,
                            providerId: user.providerId,
                            refreshToken: user.refreshToken,
                            uid: user.uid,
                            isAdmin: user.isAdmin,
                        }
                        commit('SET_USER', profile)
                    })
            })
            .catch(err => Promise.reject(err))
    },
    loginWithEmailAndPassword: ({ commit }, credentials) => {
        return auth
            .signInWithEmailAndPassword(credentials.email, credentials.password)
            .catch(err => Promise.reject(err))
    },
    loginWithGoogle: () => {
        const provider = new firebase.auth.GoogleAuthProvider()
        return auth
            .signInWithPopup(provider)
            .catch(err => Promise.reject(err))
    },
    resetPassword: ({ commit }, credentials) => {
        return auth
            .sendPasswordResetEmail(credentials.email)
            .catch(err => Promise.reject(err))
    },
    signOut: () => {
        return auth
            .signOut()
            .catch(err => console.error(err))
    },
    setCurrentUser: ({ commit }, user) => commit('SET_CURRENT_USER', user),
    editCurrentUser: ({ commit }, user) => commit('EDIT_CURRENT_USER', user),
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
